import {
  Session,
  useCountriesByKeys,
  translateIncentiveText,
  usePlanIncentives,
  PlanIncentive,
} from '@app/api';
import { Chip, TextLoadingPlaceholder } from '@app/core';
import { Theme } from '@app/theme';
import { createUseStyles } from 'react-jss';
import { useDefaultSessionLink } from 'lib/planAndOffer';
import { useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Input, InputLabel } from 'components';
import { useTranslation } from 'react-i18next';

interface Props {
  className?: string;
  session: Session;
  sessionLinkId?: string;
  label?: string;
  duration?: number;
  overGray?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  list: {
    display: 'flex',
    gap: theme.marginBase,
    flexDirection: 'column',
  },
  line: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    padding: theme.marginBase,

    display: 'flex',
    alignItems: 'center',
    gap: theme.marginBase,
    flexWrap: 'wrap',
  },
  lineOverGray: {
    backgroundColor: theme.colors.white,
  },
  country: {
    flex: 1,
    minWidth: 150,
  },
  chipIcon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase / 2,
  },
}));

export const CountryIncentiveDisplay = ({
  session,
  label,
  duration,
  sessionLinkId: sessionLinkIdProps,
  overGray,
  className,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { data: allPlanIncentives, isLoading } = usePlanIncentives(
    session.planId,
    duration,
  );
  const defaultLink = useDefaultSessionLink(session.id);
  const countriesByKeys = useCountriesByKeys();
  const previousValuesRef = useRef<PlanIncentive[]>([]);

  const sessionLinkId = sessionLinkIdProps || defaultLink?.id;

  const updatedPlanIncentives = useMemo(() => {
    if (!allPlanIncentives) {
      return [];
    }
    return allPlanIncentives.filter(
      (pi) => !pi.sessionLinkId || pi.sessionLinkId === sessionLinkId,
    );
  }, [allPlanIncentives, defaultLink]);

  if (updatedPlanIncentives?.length) {
    previousValuesRef.current = updatedPlanIncentives;
  }
  const planIncentives = previousValuesRef.current;

  const allEquals = useMemo(() => {
    if (!planIncentives.length) {
      return false;
    }

    const first = planIncentives[0];
    return !planIncentives.some((pi) => {
      return (
        pi.incentive.amount !== first.incentive.amount ||
        pi.incentive.currencyCode !== first.incentive.currencyCode
      );
    });
  }, [planIncentives]);

  if (allEquals && planIncentives.length) {
    return (
      <Input
        label={label}
        noBottomMargin
        disabled
        value={translateIncentiveText(
          t,
          planIncentives[0].incentive,
          session.locale,
        )}
      />
    );
  }

  return (
    <div className={className}>
      <InputLabel label={label} />
      <div className={classes.list}>
        {planIncentives.map((planIncentive) => (
          <div
            className={classNames(classes.line, {
              [classes.lineOverGray]: overGray,
            })}
          >
            <div className={classes.country}>
              {!!planIncentive.countryKey && (
                <Chip color="green">
                  {countriesByKeys[planIncentive.countryKey]?.text}
                </Chip>
              )}
            </div>
            {isLoading ? (
              <TextLoadingPlaceholder
                sample={translateIncentiveText(
                  t,
                  planIncentive.incentive,
                  session.locale,
                )}
                overGray
              />
            ) : (
              <Chip color="inkSecondary" overGray={!overGray}>
                <i className={classNames('ri-gift-line', classes.chipIcon)} />
                {translateIncentiveText(
                  t,
                  planIncentive.incentive,
                  session.locale,
                )}
              </Chip>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
